/* ── Ambassadors hub ─────────────────────────────────────────────────────────
   Modal engine, deep links, share, and the lazy video handling for both the
   ambassadors grid and the ocean professionals grid.

   Everything lives on one namespaced object. The section loads this file once
   per instance, so the guard at the bottom makes a second load a refresh rather
   than a second set of listeners.

   Nothing here is required for the content to exist. Every profile is already
   in the HTML, this only decides what is shown and when.
  ───────────────────────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  var NAMESPACE = "SharkEyesAmbassadors";

  /* ── Environment ─────────────────────────────────────────────────────────── */

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function saveData() {
    var c = navigator.connection || navigator.webkitConnection;
    return !!(c && c.saveData);
  }

  function isCoarsePointer() {
    return window.matchMedia("(pointer: coarse)").matches;
  }

  /* Resolve what a card should actually do, as opposed to what it asked for.
     Reduced motion and Save-Data always win, and hover is meaningless on touch. */
  function effectiveBehaviour(declared) {
    if (prefersReducedMotion() || saveData()) return "poster-click";
    if (declared === "hover" && isCoarsePointer()) return "poster-click";
    return declared || "autoplay-loop";
  }

  /* ── Small helpers ───────────────────────────────────────────────────────── */

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  var FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "video[controls]",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  function focusableIn(el) {
    return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetParent !== null || node === document.activeElement;
    });
  }

  /* ── Video ───────────────────────────────────────────────────────────────── */

  function ensureSrc(video) {
    if (!video.getAttribute("src") && video.dataset.ambSrc) {
      video.setAttribute("src", video.dataset.ambSrc);
    }
  }

  function playCardVideo(card, video) {
    ensureSrc(video);
    var p = video.play();
    if (p && typeof p.catch === "function") {
      p.then(function () {
        card.classList.add("amb-is-playing");
      }).catch(function () {
        /* Autoplay refused. Fall back to a tap target rather than a dead card. */
        card.dataset.ambMode = "poster-click";
        card.classList.remove("amb-is-playing");
      });
    }
  }

  function pauseCardVideo(card, video) {
    if (!video.paused) video.pause();
    card.classList.remove("amb-is-playing");
  }

  function embedUrl(kind, raw) {
    var id = raw;
    var m;
    if (kind === "youtube") {
      m = raw.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
      if (m) id = m[1];
      return (
        "https://www.youtube.com/embed/" +
        encodeURIComponent(id) +
        "?autoplay=1&mute=1&playsinline=1&rel=0"
      );
    }
    m = raw.match(/(?:vimeo\.com\/)(?:video\/)?(\d+)/);
    if (m) id = m[1];
    return "https://player.vimeo.com/video/" + encodeURIComponent(id) + "?autoplay=1&muted=1";
  }

  /* Iframes are only ever created on a real interaction. Loading ten of them up
     front is what makes pages like this feel broken on a phone. */
  function swapFacade(facade) {
    if (facade.dataset.ambSwapped) return;
    facade.dataset.ambSwapped = "true";
    var frame = document.createElement("iframe");
    frame.src = embedUrl(facade.dataset.ambEmbed, facade.dataset.ambEmbedId || "");
    frame.title = "Video";
    frame.loading = "lazy";
    frame.allow = "autoplay; encrypted-media; picture-in-picture";
    frame.allowFullscreen = true;
    frame.style.cssText = "position:absolute;inset:0;width:100%;height:100%;border:0;";
    facade.style.position = "relative";
    facade.appendChild(frame);
  }


  /* ── Collage hero swap ───────────────────────────────────────────────────── */

  /* Deliberately not a lightbox. A second dialog would nest a focus trap inside
     the one already running and break it. Instead the chosen tile takes over the
     main image slot, which keeps focus, scroll position and the trap untouched.
     The media box owns its aspect in CSS, so nothing moves during the swap. */

  function heroParts(modal) {
    return {
      hero: modal.querySelector("[data-amb-hero-img]"),
      video: modal.querySelector("[data-amb-modal-video]"),
      facade: modal.querySelector(".amb-modal__facade"),
      tiles: modal.querySelectorAll("[data-amb-collage]"),
    };
  }

  function clearTiles(tiles) {
    each(tiles, function (t) {
      t.setAttribute("aria-current", "false");
    });
  }

  function restoreHero(modal) {
    var p = heroParts(modal);
    clearTiles(p.tiles);
    if (!p.hero) return;
    if (p.hero.dataset.ambOrigSrc) {
      p.hero.src = p.hero.dataset.ambOrigSrc;
      if (p.hero.dataset.ambOrigSrcset) p.hero.srcset = p.hero.dataset.ambOrigSrcset;
      p.hero.alt = p.hero.dataset.ambOrigAlt || "";
      return;
    }
    /* No original of its own, so it was only ever a swap target for a video. */
    if (p.video || p.facade) {
      p.hero.hidden = true;
      if (p.video) p.video.hidden = false;
      if (p.facade) p.facade.hidden = false;
    }
  }

  function setHero(modal, tile) {
    var p = heroParts(modal);
    if (!p.hero) return;
    clearTiles(p.tiles);
    tile.setAttribute("aria-current", "true");
    p.hero.src = tile.dataset.ambFull;
    if (tile.dataset.ambFullset) p.hero.srcset = tile.dataset.ambFullset;
    p.hero.alt = tile.dataset.ambAlt || "";
    p.hero.hidden = false;
    if (p.video) {
      p.video.pause();
      p.video.hidden = true;
    }
    if (p.facade) p.facade.hidden = true;
  }

  function toggleHero(tile) {
    var modal = tile.closest(".amb-modal");
    if (!modal) return;
    var wasCurrent = tile.getAttribute("aria-current") === "true";
    var p = heroParts(modal);
    /* A profile with a collage but no hero of its own has nothing to fall back
       to, so tapping the active tile there is a no op rather than a blank box. */
    var canRestore = !!(p.hero && (p.hero.dataset.ambOrigSrc || p.video || p.facade));
    if (wasCurrent && canRestore) {
      restoreHero(modal);
    } else if (!wasCurrent) {
      setHero(modal, tile);
    }
  }

  /* Profiles with a collage but no hero media get their first tile promoted on
     open, so the slot is never empty. Doing it here rather than in Liquid is
     what keeps the image off the initial page load. */
  function seedHero(modal) {
    var p = heroParts(modal);
    if (!p.hero || p.hero.getAttribute("src")) return;
    if (p.video || p.facade) return;
    var first = p.tiles[0];
    if (first) setHero(modal, first);
  }

  /* ── The hub ─────────────────────────────────────────────────────────────── */

  function Hub() {
    this.roots = [];
    this.observer = null;
    this.activeModal = null;
    this.lastFocus = null;
    this.pushedForModal = false;
    this.scrollLocked = false;
  }

  Hub.prototype.base = function () {
    var root = this.roots[0];
    return (root && root.dataset.ambBase) || "/pages/ambassadors";
  };

  /* Both link forms resolve here. The query form is what sits in the HTML
     because it is always a 200. The pretty path is what the address bar shows
     and what the share button hands out. */
  Hub.prototype.handleFromLocation = function () {
    var base = this.base().replace(/\/$/, "");
    var params = new URLSearchParams(window.location.search);
    var q = params.get("ambassador");
    if (q) return q;
    var path = window.location.pathname.replace(/\/$/, "");
    if (path.indexOf(base + "/") === 0) {
      return path.slice(base.length + 1).split("/")[0];
    }
    return null;
  };

  /* A grid can opt out of touching the address bar. Used where it is a teaser on
     another page, so opening a profile cannot rewrite that page's URL. */
  function deepLinksOn(node) {
    var root = node && node.closest ? node.closest("[data-amb-root]") : null;
    return !root || root.getAttribute("data-amb-deeplinks") !== "false";
  }

  Hub.prototype.findModal = function (handle) {
    if (!handle) return null;
    /* Handles are kebab case by contract, but never trust a value that arrived
       from the address bar. */
    if (!/^[A-Za-z0-9_-]+$/.test(handle)) return null;
    return document.querySelector('[data-amb-modal="' + handle + '"]');
  };

  /* ── Scroll lock ─────────────────────────────────────────────────────────── */

  Hub.prototype.lockScroll = function () {
    if (this.scrollLocked) return;
    /* Reserve the scrollbar width so opening a profile does not shift the page. */
    var gap = window.innerWidth - document.documentElement.clientWidth;
    this.prevOverflow = document.body.style.overflow;
    this.prevPadding = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    if (gap > 0) document.body.style.paddingRight = gap + "px";
    this.scrollLocked = true;
  };

  Hub.prototype.unlockScroll = function () {
    if (!this.scrollLocked) return;
    document.body.style.overflow = this.prevOverflow || "";
    document.body.style.paddingRight = this.prevPadding || "";
    this.scrollLocked = false;
  };

  /* ── Open and close ──────────────────────────────────────────────────────── */

  Hub.prototype.open = function (modal, options) {
    options = options || {};
    if (!modal || this.activeModal === modal) return;
    if (this.activeModal) this.close({ fromHistory: true, keepFocus: true });

    this.lastFocus = options.trigger || document.activeElement;
    modal.hidden = false;
    this.lockScroll();
    this.activeModal = modal;

    var video = modal.querySelector("[data-amb-modal-video]");
    if (video) ensureSrc(video);
    seedHero(modal);

    var targets = focusableIn(modal);
    var close = modal.querySelector("[data-amb-close]:not(.amb-modal__backdrop)");
    (close || targets[0] || modal).focus({ preventScroll: true });

    if (!options.fromHistory && deepLinksOn(modal)) {
      var deep = modal.dataset.ambDeepLink;
      if (deep) {
        window.history.pushState({ ambassador: modal.dataset.ambModal }, "", deep);
        this.pushedForModal = true;
      }
    }
  };

  Hub.prototype.close = function (options) {
    options = options || {};
    var modal = this.activeModal;
    if (!modal) return;

    var video = modal.querySelector("[data-amb-modal-video]");
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    each(modal.querySelectorAll("iframe"), function (frame) {
      frame.parentNode.removeChild(frame);
    });
    each(modal.querySelectorAll("[data-amb-embed]"), function (facade) {
      delete facade.dataset.ambSwapped;
    });

    restoreHero(modal);

    modal.hidden = true;
    this.activeModal = null;
    this.unlockScroll();

    if (!options.keepFocus && this.lastFocus && document.contains(this.lastFocus)) {
      this.lastFocus.focus({ preventScroll: true });
    }
    this.lastFocus = null;

    if (!options.fromHistory && this.pushedForModal && deepLinksOn(modal)) {
      window.history.pushState({ ambassador: null }, "", this.base());
      this.pushedForModal = false;
    }
  };

  /* ── Focus trap ──────────────────────────────────────────────────────────── */

  Hub.prototype.trapFocus = function (event) {
    if (!this.activeModal || event.key !== "Tab") return;
    var panel = this.activeModal.querySelector(".amb-modal__panel") || this.activeModal;
    var items = focusableIn(panel);
    if (!items.length) {
      event.preventDefault();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ── Share ───────────────────────────────────────────────────────────────── */

  Hub.prototype.toast = function (message) {
    var existing = document.querySelector(".amb-toast");
    if (existing) existing.parentNode.removeChild(existing);
    var wrap = document.createElement("div");
    wrap.className = "amb amb-toast";
    wrap.setAttribute("role", "status");
    wrap.setAttribute("aria-live", "polite");
    var inner = document.createElement("div");
    inner.className = "amb-toast__inner";
    inner.textContent = message;
    wrap.appendChild(inner);
    document.body.appendChild(wrap);
    setTimeout(function () {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    }, 2600);
  };

  Hub.prototype.share = function (button) {
    var url = button.dataset.ambShare;
    var name = button.dataset.ambShareName || "Shark Eyes";
    var self = this;

    if (navigator.share && isCoarsePointer()) {
      navigator.share({ title: name, url: url }).catch(function () {
        /* Sheet dismissed. Nothing to report. */
      });
      return;
    }

    var done = function () {
      self.toast("Link copied");
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, function () {
        self.toast("Could not copy the link");
      });
      return;
    }

    var field = document.createElement("textarea");
    field.value = url;
    field.setAttribute("readonly", "");
    field.className = "amb-visually-hidden";
    document.body.appendChild(field);
    field.select();
    try {
      document.execCommand("copy");
      done();
    } catch (e) {
      this.toast("Could not copy the link");
    }
    document.body.removeChild(field);
  };

  /* ── Rows clip ───────────────────────────────────────────────────────────── */

  Hub.prototype.syncClip = function (root) {
    var outer = root.querySelector(".amb-grid-outer");
    var clip = root.querySelector("[data-amb-clip]");
    var button = root.querySelector("[data-amb-reveal]");
    if (!outer || !clip) return;
    if (outer.classList.contains("amb-is-expanded")) return;

    var grid = clip.querySelector(".amb-grid");
    var clipped = grid && grid.scrollHeight - clip.clientHeight > 4;
    outer.classList.toggle("amb-is-clipped", !!clipped);
    /* The reveal is optional. A capped teaser grid has none at all. */
    if (button) button.hidden = !clipped;
  };

  Hub.prototype.expand = function (root) {
    var outer = root.querySelector(".amb-grid-outer");
    var button = root.querySelector("[data-amb-reveal]");
    if (outer) {
      outer.classList.add("amb-is-expanded");
      outer.classList.remove("amb-is-clipped");
    }
    if (button) {
      button.setAttribute("aria-expanded", "true");
      button.hidden = true;
    }
  };

  /* ── Cards ───────────────────────────────────────────────────────────────── */

  Hub.prototype.setupCards = function (root) {
    var self = this;

    each(root.querySelectorAll(".amb-card"), function (card) {
      if (card.dataset.ambReady) return;
      card.dataset.ambReady = "true";

      var video = card.querySelector("[data-amb-video]");
      var facade = card.querySelector("[data-amb-embed]");
      var declared = video ? video.dataset.ambBehaviour : "poster-click";
      var mode = effectiveBehaviour(declared);
      card.dataset.ambMode = mode;

      if (facade) {
        /* An embed can only ever be started deliberately. */
        card.dataset.ambMode = "poster-click";
      }

      if (video && mode === "hover") {
        var start = function () {
          playCardVideo(card, video);
        };
        var stop = function () {
          pauseCardVideo(card, video);
        };
        card.addEventListener("mouseenter", start);
        card.addEventListener("focusin", start);
        card.addEventListener("mouseleave", stop);
        card.addEventListener("focusout", stop);
      }

      if (self.observer && (video || facade)) {
        self.observer.observe(card);
      }
    });
  };

  Hub.prototype.onIntersect = function (entries) {
    entries.forEach(function (entry) {
      var card = entry.target;
      var video = card.querySelector("[data-amb-video]");
      if (!video) return;
      var mode = card.dataset.ambMode;

      if (entry.isIntersecting) {
        /* Only autoplay cards actually on screen ever get a src. */
        if (mode === "autoplay-loop") playCardVideo(card, video);
      } else {
        pauseCardVideo(card, video);
      }
    });
  };

  /* ── Wiring ──────────────────────────────────────────────────────────────── */

  Hub.prototype.init = function () {
    var self = this;

    this.roots = Array.prototype.slice.call(document.querySelectorAll("[data-amb-root]"));
    if (!this.roots.length) return;

    if (!this.observer && "IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        function (entries) {
          self.onIntersect(entries);
        },
        { rootMargin: "200px 0px", threshold: 0.15 }
      );
    }

    this.roots.forEach(function (root) {
      self.setupCards(root);
      self.syncClip(root);
    });

    if (!this.bound) {
      this.bind();
      this.bound = true;
    }

    /* A profile asked for in the URL opens straight away, without adding a
       second history entry for the state we are already in.

       handleFromLocation still understands the old pretty path form, so links
       shared before the switch keep working. replaceState then normalises the
       address bar to the query form, which is the one Shopify canonicalises. */
    var handle = this.handleFromLocation();
    if (handle) {
      var modal = this.findModal(handle);
      if (modal) {
        var canonicalUrl = this.base() + "?ambassador=" + encodeURIComponent(handle);
        window.history.replaceState({ ambassador: handle }, "", canonicalUrl);
        this.pushedForModal = true;
        this.open(modal, { fromHistory: true });
      }
    }
  };

  Hub.prototype.bind = function () {
    var self = this;

    document.addEventListener("click", function (event) {
      if (!event.target || typeof event.target.closest !== "function") return;

      var play = event.target.closest("[data-amb-play]");
      if (play) {
        var card = play.closest(".amb-card");
        var host = card || play.closest(".amb-modal__facade, .amb-modal");
        var facade = host && host.querySelector("[data-amb-embed]");
        var video = host && host.querySelector("[data-amb-video]");
        event.preventDefault();
        event.stopPropagation();
        if (facade) {
          swapFacade(facade);
        } else if (video && card) {
          playCardVideo(card, video);
        }
        return;
      }

      var tile = event.target.closest("[data-amb-collage]");
      if (tile) {
        event.preventDefault();
        toggleHero(tile);
        return;
      }

      var opener = event.target.closest("[data-amb-open]");
      if (opener) {
        var target = document.getElementById(opener.dataset.ambOpen);
        if (target) {
          event.preventDefault();
          self.open(target, { trigger: opener });
        }
        return;
      }

      if (event.target.closest("[data-amb-close]")) {
        event.preventDefault();
        self.close();
        return;
      }

      var shareButton = event.target.closest("[data-amb-share]");
      if (shareButton) {
        event.preventDefault();
        self.share(shareButton);
        return;
      }

      var reveal = event.target.closest("[data-amb-reveal]");
      if (reveal) {
        event.preventDefault();
        var root = reveal.closest("[data-amb-root]");
        if (root) self.expand(root);
      }
    });

    /* Capture phase on purpose. Something else on the page stops keydown before
       it reaches a bubble phase listener on document, verified in the browser:
       a capture listener sees Escape, a bubble listener never does. Capturing
       puts us first, and we stop the event so the theme's own drawer logic does
       not also react to a key meant for our dialog.

       Known limitation, browser imposed and not fixable here. While focus sits
       inside Chrome's native video controls, Escape is swallowed by the browser
       and is never dispatched to the DOM at all, so no listener can see it. The
       close button is the first thing focused when a profile opens and is a
       single Tab away from the video, so there is always a way out.

       Inert unless one of our dialogs is actually open. */
    document.addEventListener(
      "keydown",
      function (event) {
        if (!self.activeModal) return;
        if (event.key === "Escape" || event.key === "Esc") {
          event.preventDefault();
          event.stopPropagation();
          self.close();
          return;
        }
        self.trapFocus(event);
      },
      true
    );

    window.addEventListener("popstate", function () {
      var handle = self.handleFromLocation();
      var modal = self.findModal(handle);
      if (modal) {
        self.open(modal, { fromHistory: true });
      } else {
        self.close({ fromHistory: true });
      }
    });

    window.addEventListener(
      "resize",
      debounce(function () {
        self.roots.forEach(function (root) {
          self.syncClip(root);
        });
      }, 150)
    );

    /* Theme editor support. */
    document.addEventListener("shopify:section:load", function () {
      self.init();
    });
  };

  /* ── Boot ────────────────────────────────────────────────────────────────── */

  if (window[NAMESPACE]) {
    /* The file is referenced by each section, so a second execution is a
       refresh of the new markup, never a second set of listeners. */
    window[NAMESPACE].init();
    return;
  }

  var hub = new Hub();
  window[NAMESPACE] = hub;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      hub.init();
    });
  } else {
    hub.init();
  }
})();
